<!-- Displays an autocomplete box to search for a station.
@props placeholder - A String to set the placeholder text in the input box.
@props hideBorder - A boolean that when set to true hides the default Vuetify border around the input field.
@prop label - A String that will be set to the autocomplete label. -->
<script setup lang="ts">
import type { Ref } from "vue";
import { ref, getCurrentInstance, onMounted } from "vue";
import type { PlaceItem } from "@/models/basket/PlaceItem";
import gql from "graphql-tag";
import { useApolloClient } from "@vue/apollo-composable";

const { client } = useApolloClient();
const isLoading: Ref<boolean> = ref(false);
const places: Ref<Array<PlaceItem>> = ref<Array<PlaceItem>>([]);
const props = defineProps<{
    placeholder: string;
    modelValue: PlaceItem | null;
    hideBorder?: boolean;
    label?: string;
}>();
const emit = defineEmits(["update:modelValue"]);
const graphqlUrl = ref("Unknown");
const instance = getCurrentInstance();

function autocompleteVariant() {
    return props.hideBorder ? "solo" : "outlined";
}

function stationSearch(query: string) {
    return new Promise(async (resolve, reject) => {
        try {
            const result = await client.query({
                query: gql`
                query placeQuery {
                    placeQuery(
                        query: "${query}"
                        purpose: ORIGIN
                        sort: RELEVANCE
                        limit: 30
                    ) {
                        id
                        name
                        nlc
                    }
                }`,
            });

            resolve(result.data.placeQuery);
            return;
        } catch (error) {
            console.error(error);
            reject();
            return;
        }
    });
}

async function getStations(searchTerm: string) {
    // If clear button is clicked, searchTerm is null, and nothing to do
    if (searchTerm === null) {
        return;
    }
    const trimmedSearchTerm = searchTerm.trim();

    // Don't run a search if the search term is empty
    // Don't run another search if the search term is the same as the current selected station (autocompletes run a search on click)
    if (trimmedSearchTerm === "") {
        places.value = getPopularStations();
        return;
    }

    isLoading.value = true;

    try {
        const getPlacesResponse = await stationSearch(trimmedSearchTerm);
        places.value = getPlacesResponse;
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }
}

function getPopularStations(): PlaceItem[] {
    let data = [];
    data.push({ isHeader: true, name: "Popular Places" });

    if (graphqlUrl.value === "http://localhost:9999/gqlapi" || graphqlUrl.value === "https://gql.test.rocketonrail.co.uk/graphql") {
        data.push(
            {
                id: "9c8456df-0662-452a-a871-9a2f22641639",
                name: "London Terminals",
                nlc: "1072",
            },
            {
                id: "b1692227-8db9-4457-af64-563c82ed481b",
                name: "Birmingham New Street, Moor Street or Snow Hill",
                nlc: "0418",
            },
            {
                id: "c244eab5-b534-4eaf-b17c-a45dfbb6d6a3",
                name: "Manchester Piccadilly, Victoria, Oxford Road or Deansgate",
                nlc: "0438",
            },
            {
                id: "700672d4-1491-443d-99e5-916c8b1e8545",
                name: "Edinburgh",
                nlc: "9328",
            }
        );
    } else if (graphqlUrl.value === "https://gql.staging.rocketonrail.co.uk/graphql") {
        data.push(
            {
                id: "d3064c79-276c-4a7f-946c-c9d56f1adf95",
                name: "London Terminals",
                nlc: "1072",
            },
            {
                id: "ae321cb6-a797-4a6d-b66e-b5c76a6c56c6",
                name: "Birmingham New Street, Moor Street or Snow Hill",
                nlc: "0418",
            },
            {
                id: "ea4a015c-67c3-4be9-b336-9652cb181f11",
                name: "Manchester Piccadilly, Victoria, Oxford Road or Deansgate",
                nlc: "0438",
            },
            {
                id: "b4942904-c5a2-4378-bb2c-0518a8260022",
                name: "Edinburgh",
                nlc: "9328",
            }
        );
    } else {
        // Production
        data.push(
            {
                id: "938e539e-b5bf-4655-aadd-96d06835ac4c",
                name: "London Terminals",
                nlc: "1072",
            },
            {
                id: "f817b05e-ad97-45bf-8d6e-46d60dbfc019",
                name: "Birmingham New Street, Moor Street or Snow Hill",
                nlc: "0418",
            },
            {
                id: "e73a46e3-1fcc-4627-aa49-52d7ccc1e7c9",
                name: "Manchester Piccadilly, Victoria, Oxford Road or Deansgate",
                nlc: "0438",
            },
            {
                id: "8adb5f17-edd5-4522-b74e-21b5b41809d9",
                name: "Edinburgh",
                nlc: "9328",
            }
        );
    }

    return data;
}

onMounted(() => {
    if (instance?.appContext?.config?.globalProperties?.$graphqlUrl) {
        graphqlUrl.value = instance.appContext.config.globalProperties.$graphqlUrl;
        places.value = getPopularStations();
    }
});
</script>

<template>
    <v-autocomplete :variant="autocompleteVariant()" :items="places" @update:search="getStations" @update:model-value="(v) => emit('update:modelValue', v)" :model-value="props.modelValue" :placeholder="props.placeholder" hide-details item-title="name" item-value="id" :loading="isLoading" density="compact" return-object no-filter bg-color="transparent" auto-select-first :flat="hideBorder" :label="props.label" no-data-text="No stations found" persistent-clear clearable clear-icon="mdi-close">
        <template v-slot:item="{ props: itemProps, item }">
            <v-list-item v-if="item.raw.isHeader" disabled class="header-item">
                <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-else v-bind="itemProps"> </v-list-item>
        </template>
    </v-autocomplete>
</template>

<style scoped lang="scss"></style>
